import {
  applyAcrossBlocks,
  type BlockAt,
  type BlockOp,
  type BlockOpResult,
  type DoczFragment,
  type DoczModule,
  encodeBlock,
  type FragmentRun,
  type ListItem,
  type ListItemDto,
  type ListState,
  type ListStyle,
  MIXED,
  type PaletteBlock,
  parseBlockAddress,
  resolveListState,
  type StructuredBlock,
  type StyleSlice,
} from "@norkron/web-docz";
import {
  canvasDeleteStructuredBlock,
  canvasDoczBlocks,
  type CanvasEditorHandle,
  canvasReplaceStructuredBlock,
  handleFromBridge,
} from "@norkron/web-page-layout";

/**
 * `canvasListLayer` — the CANVAS half of the ribbon's Lists group.
 *
 * ## What was broken
 *
 * `<CanvasElaborateEditor>` published five `() => null` stubs and
 * `canvasCapabilities` declared `lists: { available: false, why: "STUB: list blocks
 * are not canvas-addressable yet (TODO C2/C4) — every verb declines." }`, so every
 * Lists command rendered GREY under the tooltip "This document engine does not
 * support lists and numbering yet."
 *
 * **That stated reason was false.** List blocks have been canvas-addressable since
 * the sub-block address landed: web-docz's `legacyFromStructuredBlock` projects one
 * `list_item` block PER ITEM carrying its own address (`L::li:0`, `L::li:1` —
 * `block-address.ts`), web-page-layout's `block-map.ts` promotes `list_item` into
 * the AUX `listItem` ordinal namespace, and `isCanvasAddressableKind` has answered
 * true for it all along. A caret has been able to sit in a list item, type in it and
 * bold it for as long as the canvas has existed.
 *
 * The REAL blocker sat one layer down, and it is now gone. The four AUTHORED list
 * properties — `ListItem.checked`, `List.start_override`, `ListOptions.bullet_char`
 * and `ListItem.level` — died at the docz→flex boundary inside pdfz, so the engine
 * painted `•` no matter what the model said. pdfz `1e096a4` carries all four through
 * (☐ U+2610 / ☑ U+2611 for a task item, a shifted ordinal for a restart, a custom
 * glyph, a real nesting indent), web-page-layout `f79531a` re-vendored the blob, and
 * `web/pdfz-layout.wasm.lock` moved to it. Only after that could a list verb change
 * a single pixel — which is why flipping the capability without moving the lock
 * would have shipped a correct model over an unchanged raster.
 *
 * ## Why the fold, and never `selection.focus.blockOrd`
 *
 * A list operation is BLOCK-STRUCTURAL over a RANGE. `canvasFormatLayer.setBlockKind`
 * still resolves a SINGLE ordinal, so "bullet-list these three selected paragraphs"
 * would convert exactly one and silently leave the other two as paragraphs — the
 * identical defect cross-block comments had before `placeComment` was routed through
 * `applyAcrossSlices`.
 *
 * So every verb here runs through web-docz's {@link applyAcrossBlocks}: the
 * BLOCK-scoped sibling of the run-scoped fold, taking the SAME `StyleSlice[]`
 * coordinate `EditorCore.selectionSlices()` already produces and returning the SAME
 * `SliceApplyResult`. Per-slice independence, ordinal coalescing, "zero delta is not
 * an application", "a refused commit is DECLINED and never counted" — all upstream,
 * none of it re-derived here.
 *
 * ## The one implementation, not a second one
 *
 * RULE ZERO all the way down. The numbering / level / checkbox algebra executes in
 * `docz.wasm` behind web-docz's six list verbs (`setListStyle`, `setListItemLevel`,
 * `restartList`, `setStartOverride`, `setListBulletChar`, `toggleListItemChecked`);
 * the selection→block coordinate is `EditorCore.selectionSlices()`; the traversal is
 * `applyAcrossBlocks`; the readout is `resolveListState`; the install is
 * `canvasReplaceStructuredBlock` + `EditorCore.applyBridge`, exactly how
 * `canvasTableLayer` lands a table verb. This file resolves addresses and splices
 * items. It owns no numbering model and frames no list bytes.
 *
 * ## The refusal contract
 *
 * Every verb returns `DoczFragment | null`, and `null` means NOTHING MOVED — never a
 * fabricated success. A verb declines when: no editor is mounted; the loaded
 * `docz.wasm` lacks the list verbs (`supportsListVerbs` — an honest degrade, never a
 * hand-rolled TS numbering); the bridge is not docz-backed or is DECORATED (a
 * read-only surface must not rewrite the document); the selection covers no block
 * this surface can answer for; or every touched block declined.
 *
 * AND A REFUSAL IS NOW AUDIBLE. Every `reason` string this file builds used to be
 * discarded — `apply` read only `res.applied.length` and the ribbon ignored the
 * return — so a refused command was indistinguishable from a broken one. They travel
 * `CanvasListLayerDeps.refuse`, the SAME channel a refused structural insert already
 * uses. `null` still means nothing moved; it no longer means nothing was said.
 *
 * ## Which verbs CONVERT, and which need a list that already exists
 *
 * The ribbon's own grammar draws the line and this file follows it, so the two cannot
 * disagree about what a group member does:
 *
 *   · `list-type` (bullet · ordered · CHECKLIST) — "what KIND of list is this block?"
 *     A question about a BLOCK, so all three CONVERT a run-bearing leaf.
 *   · `list-ops` (restart · bullet-glyph) — a channel of a list. REFUSE body text.
 *   · `list-nest` (demote · promote) — a level of an item. REFUSE body text.
 *
 * `toggleTaskList` used to be the one cluster member that behaved like a channel
 * verb, which is why pressing Checklist on body text did nothing, forever, silently.
 *
 * AND THE DOM SURFACE ALREADY AGREED. `web-elaborate-editor`'s controller has
 * converted on Checklist since it shipped — `toggleTaskList(): paletteEditor
 * .toggleTaskList() ?? toggleListItem()`, pinned by its own
 * `structured-blocks.test.ts` ("toggleTaskList … fall back to a list_item on a plain
 * paragraph"). The canvas was the outlier, so this is not a new opinion about what
 * Checklist means; it is the two surfaces agreeing again.
 *
 * ⚠ ONE DELIBERATE DIVERGENCE FROM THE DOM: its `restartList` also falls back to
 * `ensureListItem()`, which converts the paragraph and then does NOT apply the start
 * the user asked for — a half-honoured gesture that looks like it worked. This layer
 * refuses instead, out loud. "Restart at 5" that produces a list starting at 1 is a
 * worse answer than "there is no numbering here to restart".
 */

/** A `list` PaletteBlock, narrowed off the union. */
type ListBlock = Extract<PaletteBlock, { readonly kind: "list" }>;

export interface CanvasListLayerDeps {
  /** The live canvas editor handle (`null` before mount / after dispose). */
  getEditor(): CanvasEditorHandle | null;
  /**
   * THE REFUSAL CHANNEL — where a declined verb says why.
   *
   * Every verb below builds a `declined[].reason` sentence ("not a list — no marker
   * to set", "docz declined nest by 1 (err 7)") and every one of them used to be
   * DISCARDED: `apply` looked only at `res.applied.length`, returned `null`, and
   * `catalog.ts` ignored the return. A refused ribbon command therefore reached the
   * user as nothing at all — the same silent no-op the Lists group spent its whole
   * life being.
   *
   * This is the SAME seam a refused structural insert already uses
   * (`canvasInsertLayer`'s `refuse` → `<DoczCanvas>` toast), not a second one.
   * Optional so a test rig or a headless caller can leave it off.
   */
  refuse?(message: string): void;
}

export interface CanvasListLayer {
  /** Set the marker style over every block the selection touches, CONVERTING a
   *  run-bearing leaf (paragraph / heading) into a list when it is not one yet. */
  setListStyle(style: ListStyle): DoczFragment | null;
  /** Demote (+1) / promote (−1) every touched list item's nesting level. */
  setListItemLevel(delta: number): DoczFragment | null;
  /** Restart numbering at `start`, or clear the override when `null`. A CHANNEL verb:
   *  body text names no numbering instance, so it declines by name. */
  restartList(start: number | null): DoczFragment | null;
  /** Cycle every touched item's checkbox — docz's own null→false→true→false — and
   *  CONVERT a run-bearing leaf into an unticked task item, exactly as its `list-type`
   *  cluster siblings `setListStyle('bullet'|'decimal')` convert one. */
  toggleTaskList(): DoczFragment | null;
  /** Set a custom bullet glyph on every touched list. */
  setBulletGlyph(glyph: string): DoczFragment | null;
  /** The list state over the selection — everything a Lists control needs to render
   *  itself honestly. {@link resolveListState} over the same slices, nothing else. */
  listState(): ListState;
}

// ─── item helpers (the DTO's own two shapes, never a third) ───────────────────

/** An item's runs. A bare run slice IS its runs; a DTO carries them. */
export function itemRuns(item: ListItem): readonly FragmentRun[] {
  return Array.isArray(item) ? item : (item as ListItemDto).runs;
}

/** An item's nesting level — the DTO's documented default 0 for a bare slice. */
export function itemLevel(item: ListItem): number {
  return Array.isArray(item) ? 0 : ((item as ListItemDto).level ?? 0);
}

/** An item's checkbox state. `null` is a VALUE (an ordinary item), not absence. */
export function itemChecked(item: ListItem): boolean | null {
  return Array.isArray(item) ? null : ((item as ListItemDto).checked ?? null);
}

/** A block's runs when it is a run-bearing leaf, else `null` (a table, an image, a
 *  rule and a code island carry no run slice a list verb could be aimed at). */
function leafRuns(block: PaletteBlock): readonly FragmentRun[] | null {
  if (block.kind !== "paragraph" && block.kind !== "heading") return null;
  return (block as { runs?: readonly FragmentRun[] }).runs ?? [];
}

/**
 * A ONE-ITEM list carrying every list-level channel of `list` plus item `i`.
 *
 * This is exactly the projection `applyAcrossBlocks` documents for a surface that
 * addresses a container per item ("a `list` addressed as `L::li:0`, `L::li:1` …
 * answers each ordinal with the single-item block at it"). EVERY list-level channel
 * travels — dropping `numId`/`bulletChar` here would let a verb that only touches
 * `checked` silently reset the list's custom glyph on the way back.
 */
function itemProjection(list: ListBlock, i: number): ListBlock | null {
  const item = list.items[i];
  if (item === undefined) return null;
  return { ...list, items: [item] };
}

/** Do two lists share every channel that a SINGLE list block can only have one of?
 *  The predicate {@link coalesce} merges on. `startOverride` is deliberately absent:
 *  a list that restarts its numbering is a NEW list by definition. */
function sameListChannels(a: ListBlock, b: ListBlock): boolean {
  return a.style === b.style && a.numId === b.numId && a.bulletChar === b.bulletChar;
}

// ─── the layer ────────────────────────────────────────────────────────────────

export function createCanvasListLayer(deps: CanvasListLayerDeps): CanvasListLayer {
  /**
   * THE COORDINATE — identical to `canvasFormatLayer`'s, deliberately.
   *
   * A COLLAPSED caret produces no slices upstream (there is no range), but a block
   * command needs no bytes: a caret in a blank paragraph is the single commonest way
   * anybody has ever started a list. So a caret becomes ONE zero-width slice, which
   * `applyAcrossBlocks` treats as a full participant — its one documented divergence
   * from the run-level fold, and the reason the Lists group is not still dead.
   */
  function slices(core: CanvasEditorHandle["core"]): StyleSlice[] {
    const { anchor, focus } = core.getState().selection;
    if (anchor.blockOrd === focus.blockOrd && anchor.byteOffset === focus.byteOffset) {
      return [{ blockOrd: focus.blockOrd, start: focus.byteOffset, end: focus.byteOffset }];
    }
    return core
      .selectionSlices()
      .map((s) => ({ blockOrd: s.blockOrd, start: s.start, end: s.end }));
  }

  /** Everything a verb needs, or `null` for an honest decline. */
  interface Ctx {
    readonly editor: CanvasEditorHandle;
    readonly module: DoczModule;
    readonly sel: readonly StyleSlice[];
    /** The top-level structured document in DOCUMENT ORDER, by id. A `Map` keeps
     *  insertion order, which is what makes {@link coalesce}'s adjacency real. */
    readonly working: Map<string, PaletteBlock>;
    /** Canvas ordinal → `(rootId, itemIndex|null)`. */
    target(blockOrd: number): { rootId: string; itemIndex: number | null } | null;
  }

  function ctx(): Ctx | null {
    const editor = deps.getEditor();
    if (!editor) return null;
    const bridge = editor.core.getState().bridge;
    const handle = handleFromBridge(bridge);
    if (!handle) return null;
    // HONEST DEGRADE, never a TS re-implementation: an older `docz.wasm` without the
    // six `dz_transform_*` list exports cannot do this, and inventing the numbering
    // here is precisely what RULE ZERO forbids.
    if (!handle.module.supportsListVerbs) return null;
    const canvas = canvasDoczBlocks(bridge);
    if (!canvas) return null;

    const working = new Map<string, PaletteBlock>();
    for (const sb of handle.toBlocks()) {
      const id = (sb as { id?: string }).id;
      // AMBIGUITY IS REFUSED, NOT GUESSED: an id that names two top-level blocks
      // cannot be spliced into, so the first wins and the duplicate resolves to a
      // block whose commit would rewrite the wrong one — `target` declines it below
      // because its own address never matches a duplicate.
      if (id != null && !working.has(id)) working.set(id, sb as PaletteBlock);
    }

    return {
      editor,
      module: handle.module,
      sel: slices(editor.core),
      working,
      target(blockOrd) {
        const id = canvas[blockOrd]?.id;
        if (id == null) return null;
        const addr = parseBlockAddress(id);
        if (addr.segments.length === 0) return { rootId: addr.rootId, itemIndex: null };
        // ONE `li:` segment and nothing else. A list nested inside a blockquote
        // (`Q::bq:0::li:1`) addresses a child OF a child: its container is not a
        // top-level block, so splicing an item back would rewrite the wrong block
        // entirely. Declining names itself in `declined` instead.
        const only = addr.segments.length === 1 ? addr.segments[0] : undefined;
        if (only?.kind !== "listItem") return null;
        return { rootId: addr.rootId, itemIndex: only.index };
      },
    };
  }

  /**
   * `BlockAt` over the WORKING copy — the block a canvas ordinal names.
   *
   * Reading from `working` rather than from the pristine document is what makes a
   * multi-item selection inside ONE list compose: item 1's op sees item 0's result,
   * so demoting three items of the same list demotes three, not one.
   */
  function blockAtOf(c: Ctx): BlockAt {
    return (blockOrd) => {
      const t = c.target(blockOrd);
      if (!t) return null;
      const root = c.working.get(t.rootId);
      if (!root) return null;
      // A top-level ordinal answers with the block itself, whatever kind it is.
      //
      // There is deliberately NO "can a list verb be aimed at this?" filter here: a
      // block that bears no runs (a rule, an image, a table wearing a paragraph's
      // clothes in the legacy projection) is refused where the refusal has a REASON
      // to give — `asList` for a conversion, the `kind !== 'list'` guard in every
      // other verb. Refusing it twice made the second guard unfalsifiable, and it
      // also poisoned {@link resolveListState}: a rule is genuinely not a list, so it
      // must contribute the REAL answer `'none'` (which is what makes a rule + list
      // selection resolve MIXED) rather than contributing nothing.
      if (t.itemIndex == null) return root;
      if (root.kind !== "list") return null;
      return itemProjection(root, t.itemIndex);
    };
  }

  /**
   * `CommitBlock` — splice the op's result back into the working document.
   *
   * The single-item projection travels BACK the way it came: its one item replaces
   * item `i`, and its LIST-LEVEL channels (style / numId / startOverride /
   * bulletChar) become the list's, because a list has exactly one of each and the op
   * was asked about the list, not about the item.
   */
  function commitOf(c: Ctx, touched: Set<string>) {
    return (blockOrd: number, next: PaletteBlock): boolean => {
      const t = c.target(blockOrd);
      if (!t) return false;
      const root = c.working.get(t.rootId);
      if (!root) return false;
      if (t.itemIndex == null) {
        c.working.set(t.rootId, next);
        touched.add(t.rootId);
        return true;
      }
      if (root.kind !== "list" || next.kind !== "list") return false;
      const nextItem = next.items[0];
      if (nextItem === undefined) return false;
      const items = root.items.slice();
      items[t.itemIndex] = nextItem;
      c.working.set(t.rootId, { ...next, items } as ListBlock);
      touched.add(t.rootId);
      return true;
    };
  }

  /**
   * MERGE adjacent lists the gesture produced into one list block.
   *
   * Converting three selected paragraphs yields three ONE-ITEM lists, and pdfz
   * numbers each `List` block from its own start — so a "Numbered list" over three
   * paragraphs would paint `1.` `1.` `1.`. There is no cross-block continuation to
   * lean on: `List.num_id` exists in the docz IR but `pdfz/src/flex.zig` never reads
   * it, so shared-numbering is not a thing this engine can be asked for. Merging is
   * therefore not a nicety, it is the only spelling of "these three lines are one
   * numbered list" the raster can express.
   *
   * Conservative by construction: a run merges only when EVERY member is a list,
   * every member shares {@link sameListChannels}, no member after the first carries
   * a `startOverride` (a restart is what "a new list" MEANS), and at least one
   * member was touched by THIS gesture — so two lists the user never addressed are
   * never silently welded together.
   *
   * Returns the ids that were absorbed (their items now live in the run's first
   * block, which keeps its own id so the caret's address survives).
   */
  function coalesce(c: Ctx, touched: Set<string>): string[] {
    const ids = [...c.working.keys()];
    const dropped: string[] = [];
    let i = 0;
    while (i < ids.length - 1) {
      const aId = ids[i] as string;
      const bId = ids[i + 1] as string;
      const a = c.working.get(aId);
      const b = c.working.get(bId);
      const joinable =
        a?.kind === "list" &&
        b?.kind === "list" &&
        sameListChannels(a, b) &&
        b.startOverride === undefined &&
        // PAIRWISE, not run-wise. A run-wise merge would fuse two lists that were
        // BOTH already in the document just because a third list beside them was
        // converted — a verb aimed at neither is not permission to rewrite both.
        (touched.has(aId) || touched.has(bId));
      if (!joinable) {
        i += 1;
        continue;
      }
      c.working.set(aId, { ...a, items: [...a.items, ...b.items] } as ListBlock);
      touched.add(aId);
      touched.delete(bId);
      c.working.delete(bId);
      dropped.push(bId);
      // `a` has absorbed `b`; re-examine the SAME position against what follows, so
      // three converted paragraphs collapse to one list rather than to two.
      ids.splice(i + 1, 1);
    }
    return dropped;
  }

  /**
   * Say every DISTINCT reason a block refused, once.
   *
   * Deduped because a three-block selection refusing for one reason is ONE fact
   * about the gesture, not three — three identical toasts is how a surface teaches
   * people to ignore it. Reported even on a PARTIAL success: a block that refused
   * refused, and "some of what you asked for did not happen" is precisely what the
   * user cannot see for themselves on a canvas.
   */
  function report(declined: readonly { readonly reason: string }[]): void {
    if (!deps.refuse || declined.length === 0) return;
    const said = new Set<string>();
    for (const d of declined) {
      if (said.has(d.reason)) continue;
      said.add(d.reason);
      deps.refuse(d.reason);
    }
  }

  /**
   * Run `makeOp`'s op over every touched block and install the result as ONE
   * undoable step.
   *
   * Returns the FOCUS block's new fragment, or `null` when nothing moved. Every
   * changed top-level block goes through `canvasReplaceStructuredBlock` — the same
   * structural seam a table verb lands through — and `applyBridge` relayouts the lot
   * once, so an N-block conversion is ONE undo step, not N.
   *
   * Every block that REFUSED is named on the refusal channel first ({@link report}),
   * whether or not the gesture succeeded elsewhere.
   */
  function apply(makeOp: (m: DoczModule) => BlockOp): DoczFragment | null {
    const c = ctx();
    if (!c) return null;
    if (c.sel.length === 0) return null;

    const touched = new Set<string>();
    const res = applyAcrossBlocks(c.sel, blockAtOf(c), makeOp(c.module), commitOf(c, touched));
    report(res.declined);
    if (res.applied.length === 0) return null;

    const dropped = coalesce(c, touched);

    let bridge = c.editor.core.getState().bridge;
    for (const rootId of touched) {
      const block = c.working.get(rootId);
      if (!block) continue;
      const next = canvasReplaceStructuredBlock(bridge, rootId, {
        ...block,
        id: rootId,
      } as StructuredBlock);
      // `null` = not docz-backed or DECORATED; identity = the id named no top-level
      // block. Both are honest "nothing moved", never a fabricated success.
      if (!next || next === bridge) return null;
      bridge = next;
    }
    for (const id of dropped) {
      const next = canvasDeleteStructuredBlock(bridge, id);
      if (!next || next === bridge) return null;
      bridge = next;
    }

    // The selection is re-installed explicitly: a CONVERSION moves a block out of the
    // paragraph ordinal namespace into the AUX `listItem` one, and a merge renumbers
    // the absorbed items' addresses, so `applyBridge` must re-derive the block map
    // around the caret rather than inherit a map the mutation invalidated.
    const { selection } = c.editor.core.getState();
    c.editor.core.applyBridge(bridge, { selection });

    return focusFragment(c);
  }

  /** The focus block's fragment AFTER the fold — the verb's non-null witness, and
   *  the same "here is what you are now looking at" the DOM controller returns. */
  function focusFragment(c: Ctx): DoczFragment {
    const ord = c.editor.core.getState().selection.focus.blockOrd;
    const t = c.target(ord);
    const block = t ? c.working.get(t.rootId) : undefined;
    if (!block) return { kind: "paragraph", runs: [] };
    if (block.kind === "list") {
      const item = block.items[t?.itemIndex ?? 0] ?? block.items[0];
      return { kind: "list_item", runs: item ? [...itemRuns(item)] : [] };
    }
    return { kind: "paragraph", runs: [...(leafRuns(block) ?? [])] };
  }

  /**
   * Run ONE web-docz list verb over a single-block document and unwrap it.
   *
   * The wasm status becomes a SENTENCE here rather than reaching the user as
   * "err 7" — the vocabulary boundary `BlockOpResult.reason` exists for.
   *
   * ⚠ A VERB THAT CHANGED NOTHING DID NOT APPLY. Several list verbs answer OK while
   * leaving the block alone — `setListItemLevel(-1)` on a level-0 item CLAMPS, which
   * is correct, and returns a successful result carrying an identical block. The
   * decoded block is a fresh object, so `applyAcrossBlocks`' zero-delta check (which
   * compares by identity, deliberately) could not see it: outdent on a top-level item
   * would install an identical document, report applied, and burn an undo step —
   * a fabricated success, the failure class this whole surface exists to remove.
   *
   * So a byte-identical result is returned AS THE INPUT REFERENCE, which is exactly
   * the signal the fold reads. Compared on docz's OWN canonical wire bytes
   * (`encodeBlock` — the same form the layout pass consumes), never a hand-rolled
   * deep-equal, so a channel added upstream is covered without touching this file.
   * Same discipline as `canvasTableLayer`'s `sameTable`.
   */
  function verb(
    before: PaletteBlock,
    run: () => { readonly ok: boolean; readonly value?: PaletteBlock[]; readonly errCode?: number },
    what: string,
  ): BlockOpResult {
    const res = run();
    if (!res.ok || !res.value) {
      return { ok: false, reason: `docz declined ${what} (err ${res.errCode ?? "?"})` };
    }
    const block = res.value[0];
    if (!block) return { ok: false, reason: `docz returned no block for ${what}` };
    return { ok: true, block: sameBlock(before, block) ? before : block };
  }

  /** Do two blocks carry the SAME document content? docz's own canonical wire
   *  bytes, so every channel is in scope. */
  function sameBlock(a: PaletteBlock, b: PaletteBlock): boolean {
    const x = encodeBlock(a);
    const y = encodeBlock(b);
    if (x.length !== y.length) return false;
    for (let i = 0; i < x.length; i += 1) if (x[i] !== y[i]) return false;
    return true;
  }

  /**
   * CONVERT a run-bearing leaf into a one-item list of `style`.
   *
   * There is no docz verb for "make this paragraph a list" — `setListStyle` restyles
   * a list that already exists — so the conversion is a DTO SHAPE change: the
   * paragraph's runs become the list's single item, VERBATIM. No run is rewritten,
   * no mark is dropped, nothing is computed. The moment such a verb exists upstream
   * this function becomes a call to it.
   */
  function asList(block: PaletteBlock, style: ListStyle): BlockOpResult {
    const runs = leafRuns(block);
    if (runs == null) return { ok: false, reason: "this block cannot become a list" };
    return { ok: true, block: { kind: "list", style, items: [[...runs]] } as ListBlock };
  }

  return {
    setListStyle: (style) =>
      apply((m) => (_ord, block) => {
        if (block.kind !== "list") return asList(block, style);
        // Zero delta is not an application — `applyAcrossBlocks` drops an op that
        // returns its input, which is what keeps a second press of an already-active
        // style from burning an undo step and flipping a ribbon toggle.
        if (block.style === style) return { ok: true, block };
        return verb(block, () => m.setListStyle([block], 0, style), `set list style ${style}`);
      }),

    setListItemLevel: (delta) =>
      apply((m) => (_ord, block) => {
        if (block.kind !== "list") {
          return { ok: false, reason: "not a list item — nothing to indent" };
        }
        return verb(block, () => m.setListItemLevel([block], 0, 0, delta), `nest by ${delta}`);
      }),

    restartList: (start) =>
      apply((m) => (_ord, block) => {
        if (block.kind !== "list") return { ok: false, reason: "not a list — nothing to restart" };
        // CONTINUE is `setStartOverride(undefined)` and RESTART is `restartList` —
        // the same split the DOM controller makes, and the semantically right one:
        // "restart" means a distinct numbering INSTANCE in OOXML, "continue" must
        // leave the instance alone. MEASURED against the shipped `docz.wasm`
        // (2026-07-31): `restartList(…, {})` and `setStartOverride(…, undefined)`
        // currently return byte-identical blocks — neither mints a `num_id` — so the
        // two spellings are indistinguishable TODAY. The distinction is kept anyway
        // because the day docz starts minting one, "continue" must not.
        return verb(
          block,
          () =>
            start == null
              ? m.setStartOverride([block], 0, undefined)
              : m.restartList([block], 0, { start }),
          start == null ? "continue numbering" : `restart at ${start}`,
        );
      }),

    toggleTaskList: () =>
      apply((m) => (_ord, block) => {
        // ⛔ THE SILENT NO-OP THIS REPLACES. This arm was
        // `return {ok:false, reason:"not a list — nothing to tick"}`, `apply` dropped
        // the whole gesture, and `catalog.ts` ignored the return — so pressing
        // Checklist on body text did nothing, forever, and said nothing.
        //
        // CLUSTER CONSISTENCY, not a special case. `lists.checklist` sits in the SAME
        // `list-type` cluster as `lists.bullet` and `lists.ordered`
        // (`web-elaborate-editor/src/commands/catalog.ts`), and BOTH of those convert
        // a run-bearing leaf through {@link asList}. All three answer the same
        // question — "what kind of list is this block?" — so all three convert. The
        // channel clusters (`list-ops`: restart / bullet-glyph; `list-nest`:
        // demote / promote) address a list or an item that must already exist, and
        // those still refuse body text BY NAME.
        //
        // The conversion is `bullet`-styled because a checkbox REPLACES the style
        // marker rather than sitting beside it (pdfz paints ☐/☑ in the marker slot),
        // so a checklist IS a list whose items carry `checked` — there is no third
        // marker vocabulary to invent, and `coalesce` therefore treats the new item
        // exactly as a converted bullet item, adjacency and all.
        if (block.kind !== "list") {
          const made = asList(block, "bullet");
          if (!made.ok) return made;
          const fresh = made.block;
          // The SAME docz cycle as below, run on the block that did not exist a line
          // ago: `null → false` gives the user the unticked box they pressed for. A
          // conversion that stopped at a plain bullet would be a different command.
          return verb(fresh, () => m.toggleListItemChecked([fresh], 0, 0), "start a checklist");
        }
        // docz's OWN 3-state cycle (null→false→true→false), the same call the DOM
        // controller makes, so the two surfaces cannot disagree about what a second
        // press does. Deciding a target state here would be a second checkbox model.
        //
        // ITEM 0 IS THE CARET'S ITEM, not the list's first. `blockAtOf` hands an
        // item-addressed ordinal its SINGLE-ITEM PROJECTION (`itemProjection`), so
        // index 0 names the item the caret is in; a list is never reached by a bare
        // top-level ordinal, because web-page-layout projects one canvas block PER
        // ITEM. Both halves are asserted — "it ticks THE CARET'S item" drives li:2,
        // and the PREMISE test pins the per-item projection so the day it changes
        // this index goes RED instead of quietly ticking the wrong box.
        return verb(block, () => m.toggleListItemChecked([block], 0, 0), "toggle checkbox");
      }),

    setBulletGlyph: (glyph) =>
      apply((m) => (_ord, block) => {
        if (block.kind !== "list") return { ok: false, reason: "not a list — no marker to set" };
        return verb(block, () => m.setListBulletChar([block], 0, glyph), `set bullet ${glyph}`);
      }),

    listState(): ListState {
      const c = ctx();
      // Unmounted: every field is MIXED — genuinely indeterminate, never a fabricated
      // default. A confident `style: 'none'` here would light the "not a list" state
      // over a surface that has not been asked yet.
      if (!c) {
        return {
          style: MIXED,
          level: MIXED,
          checked: MIXED,
          startOverride: MIXED,
          bulletChar: MIXED,
        };
      }
      return resolveListState(c.sel, blockAtOf(c));
    },
  };
}
